import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState, type UIEvent } from "react";
import { z } from "zod";
import {
  Upload,
  FileVideo,
  X,
  CheckCircle2,
  Sparkles,
  ShieldCheck,
  Trophy,
  Menu,
  Users,
  Award,
  PlayCircle,
  BarChart3,
  GraduationCap,
  Shield,
  Check,
  ChevronsUpDown,
} from "lucide-react";
import { DatePicker } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { BrandLogo } from "@/projects/components/brand-logo";
import usLogoSrc from "@/projects/assets/us-logo.png";
import { Button } from "@/projects/components/ui/button";
import { Input } from "@/projects/components/ui/input";
import { Textarea } from "@/projects/components/ui/textarea";
import { Label } from "@/projects/components/ui/label";
import { Progress } from "@/projects/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/projects/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/projects/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/projects/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/projects/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
} from "@/projects/components/ui/sheet";
import { toast } from "@/projects/lib/toast";
import type { Submission } from "@/projects/lib/store";
import { cn } from "@/projects/lib/utils";
import { axiosConfig } from "@/services/axiosConfig";
import { fetchTopics, type Topic } from "@/projects/lib/topicsApi";
import {
  fetchPathshalasPage,
  type Pathshala,
} from "@/projects/lib/pathshalasApi";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "રાજનગર જ્ઞાનોત્સવ 2.0 — Submit Your Educational Video" },
      {
        name: "description",
        content:
          "Rajnagar Gyanotsav 2.0 — Share your knowledge and showcase your talent by submitting an educational video.",
      },
      {
        property: "og:title",
        content: "રાજનગર જ્ઞાનોત્સવ 2.0 — Submit Your Educational Video",
      },
      {
        property: "og:description",
        content:
          "Rajnagar Gyanotsav 2.0 — Share your knowledge and showcase your talent by submitting an educational video.",
      },
    ],
  }),
  component: LandingPage,
});

const MAX_MB = 100;
const ALLOWED = [
  "video/mp4",
  "video/quicktime",
  "video/x-msvideo",
  "video/webm",
  "video/avi",
];

const schema = z.object({
  studentName: z.string().trim().min(1, "Student name is required").max(100),
  studentId: z.string().trim().min(1, "Student ID is required").max(50),
  pathshalaName: z
    .string()
    .trim()
    .min(1, "Pathshala name is required")
    .max(150),
  phoneNumber: z
    .string()
    .trim()
    .regex(/^\d{10}$/, "Enter a valid 10-digit phone number"),
  birthDate: z.string().trim().min(1, "Date of birth is required"),
  videoTitle: z.string().trim().min(1, "Video title is required").max(150),
  remark: z.string().trim().max(500).optional().or(z.literal("")),
});

/** Extract a human-readable error message from an API error response. */
function extractApiError(error: unknown): string {
  const fallback = "Failed to submit video. Please try again.";
  const data = (
    error as {
      response?: { data?: unknown };
    }
  )?.response?.data;

  if (!data) {
    return fallback;
  }
  if (typeof data === "string") {
    return data;
  }

  const collect = (value: unknown): string[] => {
    if (value == null) {
      return [];
    }
    if (typeof value === "string") {
      return [value];
    }
    if (Array.isArray(value)) {
      return value.flatMap(collect);
    }
    if (typeof value === "object") {
      return Object.values(value as Record<string, unknown>).flatMap(collect);
    }
    return [String(value)];
  };

  const obj = data as {
    error?: { message?: string; detail?: string; details?: unknown };
    message?: string;
    detail?: string;
    details?: unknown;
  };

  const details = obj.error?.details ?? obj.details;
  const detailMessages = collect(details);
  if (detailMessages.length > 0) {
    return detailMessages.join("\n");
  }

  return (
    obj.error?.message ??
    obj.error?.detail ??
    obj.message ??
    obj.detail ??
    fallback
  );
}

/** Compute age in whole years from a YYYY-MM-DD date string. */
function calcAge(birthDate: string): string {
  if (!birthDate) {
    return "";
  }
  const dob = new Date(birthDate);
  if (Number.isNaN(dob.getTime())) {
    return "";
  }
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
    age -= 1;
  }
  return age >= 0 ? String(age) : "";
}

function LandingPage() {
  return (
    <div className="bg-background min-h-screen">
      <SiteHeader />
      <Hero />
      <StatsMarquee />
      <FeatureStrip />
      <section id="submit" className="scroll-mt-20 py-14 lg:py-24">
        <div className="mx-auto max-w-3xl px-4">
          <div className="animate-rise mb-8 text-center">
            <div className="border-saffron/40 bg-saffron-soft text-saffron inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium">
              <Sparkles className="h-3.5 w-3.5" /> Open for submissions
            </div>
            <h2 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
              Student Video Submission Form
            </h2>
            <p className="text-muted-foreground mt-2 text-sm sm:text-base">
              Fill in your details and upload your educational video for review.
            </p>
          </div>
          <SubmissionForm />
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}

function SiteHeader() {
  const [open, setOpen] = useState(false);
  const features: Array<{
    to: string;
    label: string;
    icon: typeof Upload;
    tone: "saffron" | "royal" | "gold";
    badge?: string;
    isHash?: boolean;
    cta?: boolean;
  }> = [
    {
      to: "#submit",
      label: "Submit Video",
      icon: Upload,
      tone: "saffron",
      badge: "Open",
      isHash: true,
    },

    {
      to: "/login/teacher",
      label: "Teacher Panel",
      icon: GraduationCap,
      tone: "royal",
    },
    {
      to: "/login/admin",
      label: "Admin Panel",
      icon: Shield,
      tone: "gold",
      cta: true,
    },
  ];
  return (
    <header className="bg-background/80 supports-[backdrop-filter]:bg-background/70 sticky top-0 z-40 border-b backdrop-blur">
      {/* Announcement strip */}
      <div className="bg-royal hidden text-white md:block">
        <div className="mx-auto flex h-8 max-w-7xl items-center gap-4 px-4 text-[11px] font-medium">
          <span className="text-gold inline-flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" /> Season 2.0 · 2026 is live
          </span>
          <span className="h-3 w-px bg-white/20" />
          <span className="inline-flex items-center gap-1.5 text-white/85">
            <Upload className="h-3 w-3" /> Video submissions open
          </span>
          <span className="inline-flex items-center gap-1.5 text-white/85">
            <ShieldCheck className="h-3 w-3" /> Teacher verified
          </span>
          <span className="inline-flex items-center gap-1.5 text-white/85">
            <BarChart3 className="h-3 w-3" /> Live analytics
          </span>
          <span className="ml-auto text-white/70">Share · Learn · Shine</span>
        </div>
      </div>

      <div className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-4">
        <Link to="/" className="flex min-w-0 items-center gap-2.5">
          <BrandLogo className="h-9 w-9 sm:h-10 sm:w-10" />
          <div className="min-w-0">
            <div className="text-guj truncate text-sm leading-tight font-bold text-[#0f2a6b] sm:text-base">
              રાજનગર જ્ઞાનોત્સવ 2.0
            </div>
            <div className="text-[10px] font-medium tracking-widest text-[#64748b] uppercase">
              Educational Event
            </div>
          </div>
        </Link>
        <div className="flex-1" />
        <nav className="hidden items-center gap-1 lg:flex">
          {features.map(f => {
            const Icon = f.icon;
            const toneRing =
              f.tone === "saffron"
                ? "hover:border-saffron/40 hover:bg-saffron-soft"
                : f.tone === "gold"
                  ? "border-gold/60 bg-gold/10 hover:bg-gold/20"
                  : "hover:border-[#0f2a6b]/25 hover:bg-[#0f2a6b]/5";
            const inner = (
              <span
                className={cn(
                  "group relative inline-flex items-center gap-1.5 rounded-full border border-transparent px-3 py-1.5 text-sm font-medium text-[#0f2a6b] transition-all",
                  toneRing,
                  f.cta && "shadow-royal"
                )}
              >
                <Icon className="h-4 w-4 text-[#0f2a6b]" />
                {f.label}
                {"badge" in f && f.badge && (
                  <span className="bg-saffron ml-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold tracking-wider text-white uppercase">
                    {f.badge}
                  </span>
                )}
              </span>
            );
            return "isHash" in f && f.isHash ? (
              <a key={f.label} href={f.to}>
                {inner}
              </a>
            ) : (
              <Link key={f.label} to={f.to}>
                {inner}
              </Link>
            );
          })}
        </nav>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="lg:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-80">
            <SheetHeader>
              <SheetTitle className="text-guj flex items-center gap-2 text-[#0f2a6b]">
                <BrandLogo className="h-8 w-8" /> રાજનગર જ્ઞાનોત્સવ 2.0
              </SheetTitle>
            </SheetHeader>
            <div className="mt-2 text-[11px] font-semibold tracking-widest text-[#64748b] uppercase">
              Features
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {features.map(f => {
                const Icon = f.icon;
                const cls =
                  "flex flex-col items-start gap-1 rounded-xl border p-3 text-sm font-medium text-[#0f2a6b] hover:border-[#0f2a6b]/30 hover:bg-[#0f2a6b]/5";
                const inner = (
                  <>
                    <span
                      className={cn(
                        "grid h-8 w-8 place-items-center rounded-lg",
                        f.tone === "saffron"
                          ? "bg-saffron-soft text-saffron"
                          : f.tone === "gold"
                            ? "bg-gold/20 text-[#0f2a6b]"
                            : "bg-[#0f2a6b]/10 text-[#0f2a6b]"
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <span>{f.label}</span>
                    {"badge" in f && f.badge && (
                      <span className="bg-saffron rounded-full px-1.5 py-0.5 text-[9px] font-bold text-white uppercase">
                        {f.badge}
                      </span>
                    )}
                  </>
                );
                return "isHash" in f && f.isHash ? (
                  <a
                    key={f.label}
                    href={f.to}
                    onClick={() => setOpen(false)}
                    className={cls}
                  >
                    {inner}
                  </a>
                ) : (
                  <Link
                    key={f.label}
                    to={f.to}
                    onClick={() => setOpen(false)}
                    className={cls}
                  >
                    {inner}
                  </Link>
                );
              })}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section
      id="top"
      className="gradient-royal relative overflow-hidden border-b text-white"
    >
      {/* Elegant gold-rays + royal depth layer */}
      <div className="rays-bg pointer-events-none absolute inset-0" />
      {/* Cultural dot motif */}
      <div className="motif-bg absolute inset-0 opacity-70" />
      {/* Vignette for depth */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_50%,oklch(0.18_0.08_265/0.55)_100%)]" />

      <div className="relative mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:py-24 lg:grid-cols-2 lg:py-32">
        <div className="animate-rise flex flex-col justify-center">
          <div className="border-gold/40 bg-gold/10 text-gold inline-flex w-fit items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-semibold tracking-widest uppercase backdrop-blur">
            <Trophy className="h-3.5 w-3.5" /> Season 2.0 · 2026
          </div>
          <h1 className="text-guj mt-5 text-3xl leading-[1.20] font-extrabold drop-shadow-[0_2px_20px_oklch(0.18_0.08_265/0.6)] sm:text-4xl lg:text-5xl xl:text-6xl">
            શ્રી રાજનગર જૈન શ્વે.ધા.
            <br />
            ઈનામી પરીક્ષા ટ્રસ્ટ
          </h1>
          {/* <h1 className="text-guj mt-5 text-4xl leading-[1.02] font-extrabold drop-shadow-[0_2px_20px_oklch(0.18_0.08_265/0.6)] sm:text-5xl lg:text-6xl xl:text-7xl">
            રાજનગર
            <br />
            જ્ઞાનોત્સવ <span className="text-gradient-gold">2.0</span>
          </h1> */}
          <div className="mt-5 flex items-center gap-3">
            {/* <span className="bg-gold/70 h-px w-10" /> */}
            <p className="text-gold/90 text-[20px] font-medium tracking-[0.10em]">
              (ભાવિ પેઢીનું નિર્માણ)
            </p>
          </div>
          <p className="mt-4 max-w-xl text-base text-white/85 sm:text-lg lg:text-xl">
            Share Your Knowledge, Showcase Your Talent.
          </p>
          <p className="mt-3 max-w-xl text-sm text-white/70 sm:text-base">
            A celebration of learning where students submit educational videos,
            teachers verify quality, and the best get recognized.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#submit">
              <Button
                size="lg"
                className="bg-gold text-primary hover:bg-gold/90 sheen-on-hover shadow-royal font-semibold"
              >
                <Upload className="mr-2 h-4 w-4" /> Submit Your Video
              </Button>
            </a>
            <a href="#features">
              <Button
                size="lg"
                variant="outline"
                className="border-white/30 bg-white/5 text-white backdrop-blur hover:bg-white/15 hover:text-white"
              >
                <PlayCircle className="mr-2 h-4 w-4" /> How it works
              </Button>
            </a>
          </div>
        </div>

        {/* Hero art card */}
        <div className="relative hidden items-center justify-center lg:flex">
          <div
            className="border-gold/25 animate-spin-slow absolute inset-6 rounded-full border"
            aria-hidden
          />
          <div
            className="animate-spin-slow absolute inset-14 rounded-full border border-white/10 [animation-direction:reverse] [animation-duration:40s]"
            aria-hidden
          />
          <div
            className="border-gold/15 absolute inset-24 rounded-full border"
            aria-hidden
          />
          <div className="border-gold/30 shadow-royal animate-soft-float relative aspect-[4/3] w-full max-w-md rounded-2xl border bg-white/[0.06] p-3 backdrop-blur-md">
            <div className="grid h-full w-full place-items-center overflow-hidden rounded-xl bg-gradient-to-br from-white/15 to-white/[0.02]">
              <div className="text-center">
                <div className="bg-gold text-primary shadow-royal animate-glow mx-auto grid h-24 w-24 place-items-center rounded-full">
                  <FileVideo className="h-12 w-12" />
                </div>
                <div className="text-guj text-gold mt-5 text-xl font-bold">
                  વિદ્યા દાનમ્ પરમમ્ દાનમ્
                </div>
                <div className="mt-1 text-sm text-white/80">
                  Knowledge shared is knowledge multiplied
                </div>
              </div>
            </div>
            <div className="bg-background text-foreground shadow-card animate-soft-float absolute -bottom-4 -left-4 rounded-xl border p-3 [animation-delay:-2s]">
              <div className="flex items-center gap-2 text-xs font-medium">
                <div className="bg-success/10 text-success grid h-8 w-8 place-items-center rounded-full">
                  <CheckCircle2 className="h-4 w-4" />
                </div>
                Ready for Reward · +214
              </div>
            </div>
            <div className="bg-background text-foreground shadow-card animate-soft-float absolute -top-4 -right-4 rounded-xl border p-3">
              <div className="flex items-center gap-2 text-xs font-medium">
                <div className="bg-royal-soft text-royal grid h-8 w-8 place-items-center rounded-full">
                  <Users className="h-4 w-4" />
                </div>
                500+ Students
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Elegant gold divider at bottom */}
      <div className="via-gold/60 relative h-px w-full bg-gradient-to-r from-transparent to-transparent" />
    </section>
  );
}

function StatsMarquee() {
  const items = [
    { icon: Users, label: "500+ Students" },
    { icon: FileVideo, label: "1,200+ Videos" },
    { icon: ShieldCheck, label: "Teacher Verified" },
    { icon: Award, label: "Best of Best Rewards" },
    { icon: Sparkles, label: "Season 2.0 · 2026" },
  ];
  const row = [
    ...items.map(it => ({ ...it, loop: "a" as const })),
    ...items.map(it => ({ ...it, loop: "b" as const })),
  ];
  return (
    <div className="from-royal-soft via-background to-saffron-soft/60 border-b bg-gradient-to-r py-3">
      <div className="mask-fade relative overflow-hidden">
        <div className="animate-marquee flex w-max gap-10 px-4">
          {row.map(it => (
            <div
              key={`${it.loop}-${it.label}`}
              className="text-royal flex shrink-0 items-center gap-2 text-sm font-medium"
            >
              <it.icon className="text-saffron h-4 w-4" />
              <span>{it.label}</span>
              <span className="text-muted-foreground/40">✦</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FeatureStrip() {
  const items = [
    {
      icon: Upload,
      title: "Easy Uploads",
      text: "Drag & drop videos up to 100 MB.",
      tone: "bg-royal-soft text-royal",
    },
    {
      icon: ShieldCheck,
      title: "Teacher Verified",
      text: "Every entry reviewed by subject teachers.",
      tone: "bg-saffron-soft text-saffron",
    },
    {
      icon: Trophy,
      title: "Rewards",
      text: "Best submissions earn recognition.",
      tone: "bg-gold-soft text-gold",
    },
  ];
  return (
    <section id="features" className="bg-muted/30 border-b py-10 sm:py-14">
      <div className="mx-auto grid max-w-7xl gap-4 px-4 sm:grid-cols-3">
        {items.map((i, idx) => (
          <div
            key={i.title}
            style={{ animationDelay: `${idx * 120}ms` }}
            className="group hover-lift animate-rise bg-card shadow-soft flex items-start gap-3 rounded-2xl border p-4 sm:p-5"
          >
            <div
              className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl transition-transform group-hover:scale-110 ${i.tone}`}
            >
              <i.icon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="font-semibold">{i.title}</div>
              <div className="text-muted-foreground text-sm">{i.text}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function SubmissionForm() {
  const [studentName, setStudentName] = useState("");
  const [studentId, setStudentId] = useState("");
  const [pathshalaName, setPathshalaName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [remark, setRemark] = useState("");
  const [videoTitle, setVideoTitle] = useState("");
  const [topicId, setTopicId] = useState("");
  const [topics, setTopics] = useState<Topic[]>([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [pathshalas, setPathshalas] = useState<Pathshala[]>([]);
  const [pathshalasLoading, setPathshalasLoading] = useState(false);
  const [pathshalasLoadingMore, setPathshalasLoadingMore] = useState(false);
  const [pathshalaOpen, setPathshalaOpen] = useState(false);
  const [pathshalaSearch, setPathshalaSearch] = useState("");
  const [debouncedPathshalaSearch, setDebouncedPathshalaSearch] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const age = calcAge(birthDate);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [submitProgress, setSubmitProgress] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [receipt, setReceipt] = useState<Submission | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pathshalaFetchGen = useRef(0);
  const pathshalaPageRef = useRef(1);
  const pathshalaHasMoreRef = useRef(false);
  const pathshalasLoadingMoreRef = useRef(false);

  const PATHSHALA_PAGE_SIZE = 50;

  // Debounce the pathshala search box before hitting the API.
  useEffect(() => {
    const t = window.setTimeout(
      () => setDebouncedPathshalaSearch(pathshalaSearch.trim()),
      350
    );
    return () => window.clearTimeout(t);
  }, [pathshalaSearch]);

  // Load page 1 whenever the search query changes (replace list).
  useEffect(() => {
    const gen = ++pathshalaFetchGen.current;
    setPathshalasLoading(true);
    setPathshalasLoadingMore(false);
    pathshalasLoadingMoreRef.current = false;
    pathshalaPageRef.current = 1;
    pathshalaHasMoreRef.current = false;

    void (async () => {
      try {
        const { items, pagination } = await fetchPathshalasPage({
          page: 1,
          pageSize: PATHSHALA_PAGE_SIZE,
          isActive: true,
          search: debouncedPathshalaSearch || undefined,
        });
        if (gen !== pathshalaFetchGen.current) return;
        setPathshalas(items.filter(p => p.isActive));
        pathshalaPageRef.current = pagination.currentPage;
        pathshalaHasMoreRef.current =
          pagination.next != null ||
          pagination.currentPage < pagination.totalPages;
      } catch {
        if (gen !== pathshalaFetchGen.current) return;
        setPathshalas([]);
        pathshalaHasMoreRef.current = false;
      } finally {
        if (gen === pathshalaFetchGen.current) {
          setPathshalasLoading(false);
        }
      }
    })();
  }, [debouncedPathshalaSearch]);

  const loadMorePathshalas = () => {
    if (
      pathshalasLoading ||
      pathshalasLoadingMoreRef.current ||
      !pathshalaHasMoreRef.current
    ) {
      return;
    }
    const nextPage = pathshalaPageRef.current + 1;
    const gen = pathshalaFetchGen.current;
    pathshalasLoadingMoreRef.current = true;
    setPathshalasLoadingMore(true);

    void (async () => {
      try {
        const { items, pagination } = await fetchPathshalasPage({
          page: nextPage,
          pageSize: PATHSHALA_PAGE_SIZE,
          isActive: true,
          search: debouncedPathshalaSearch || undefined,
        });
        if (gen !== pathshalaFetchGen.current) return;
        const active = items.filter(p => p.isActive);
        setPathshalas(prev => {
          const seen = new Set(prev.map(p => p.id));
          return [...prev, ...active.filter(p => !seen.has(p.id))];
        });
        pathshalaPageRef.current = pagination.currentPage;
        pathshalaHasMoreRef.current =
          pagination.next != null ||
          pagination.currentPage < pagination.totalPages;
      } catch {
        // Keep existing list on load-more failure.
      } finally {
        if (gen === pathshalaFetchGen.current) {
          pathshalasLoadingMoreRef.current = false;
          setPathshalasLoadingMore(false);
        }
      }
    })();
  };

  const handlePathshalaListScroll = (e: UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 48;
    if (nearBottom) {
      loadMorePathshalas();
    }
  };

  // Load topics matching the student's age whenever a valid DOB is entered.
  useEffect(() => {
    const ageNum = Number(age);
    if (!age || Number.isNaN(ageNum)) {
      setTopics([]);
      setTopicId("");
      return;
    }
    let cancelled = false;
    setTopicsLoading(true);
    void (async () => {
      try {
        const rows = await fetchTopics({ age: ageNum, isActive: true });
        if (cancelled) {
          return;
        }
        const active = rows.filter(t => t.isActive);
        setTopics(active);
        setTopicId(prev =>
          prev && active.some(t => t.id === prev) ? prev : ""
        );
      } catch {
        if (!cancelled) {
          setTopics([]);
          setTopicId("");
        }
      } finally {
        if (!cancelled) {
          setTopicsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [age]);

  const selectedTopic = topics.find(t => t.id === topicId);
  const maxVideoMb =
    selectedTopic?.size != null && selectedTopic.size > 0
      ? selectedTopic.size
      : MAX_MB;

  const clearError = (key: string) => {
    setErrors(prev => {
      if (!prev[key]) {
        return prev;
      }
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const validateFile = (f: File, maxMb: number = maxVideoMb): string | null => {
    if (f.size > maxMb * 1024 * 1024) {
      return `File exceeds ${maxMb} MB limit.`;
    }
    const ext = f.name.split(".").pop()?.toLowerCase();
    const okExt = ["mp4", "mov", "avi", "webm"].includes(ext ?? "");
    if (!ALLOWED.includes(f.type) && !okExt) {
      return "Only MP4, MOV, AVI or WebM videos are allowed.";
    }
    return null;
  };

  const handleFile = (f: File | null) => {
    if (!f) {
      setFile(null);
      return;
    }
    const err = validateFile(f);
    if (err) {
      toast.error(err);
      setErrors(e => ({ ...e, file: err }));
      return;
    }
    setErrors(e => {
      const { file: _, ...rest } = e;
      return rest;
    });
    setFile(f);
    // Simulate upload progress
    setUploading(true);
    setProgress(0);
    const t = setInterval(() => {
      setProgress(p => {
        if (p >= 100) {
          clearInterval(t);
          setUploading(false);
          return 100;
        }
        return p + 10;
      });
    }, 120);
  };

  // Re-check uploaded file when topic size limit changes.
  useEffect(() => {
    if (!file) {
      return;
    }
    const err = validateFile(file, maxVideoMb);
    if (err) {
      setFile(null);
      setProgress(0);
      setErrors(e => ({ ...e, file: err }));
      toast.error(err);
    }
    // Only re-run when the allowed size changes (topic switch).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxVideoMb]);

  const reset = () => {
    setStudentName("");
    setStudentId("");
    setPathshalaName("");
    setPhoneNumber("");
    setBirthDate("");
    setRemark("");
    setVideoTitle("");
    setTopicId("");
    setTopics([]);
    setFile(null);
    setErrors({});
    setProgress(0);
    setSubmitProgress(0);
    setConfirmOpen(false);
  };

  const validateForm = () => {
    const result = schema.safeParse({
      studentName,
      studentId,
      pathshalaName,
      phoneNumber,
      birthDate,
      videoTitle,
      remark,
    });
    const newErrors: Record<string, string> = {};
    if (!result.success) {
      for (const iss of result.error.issues) {
        newErrors[String(iss.path[0])] = iss.message;
      }
    }
    if (!file) {
      newErrors.file = "Please upload a video file.";
    } else {
      const fileErr = validateFile(file, maxVideoMb);
      if (fileErr) {
        newErrors.file = fileErr;
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /** Save Submission → open confirmation first (do not API call yet). */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setConfirmOpen(true);
  };

  /** Confirm in modal → POST form to API. */
  const handleConfirmSave = async () => {
    if (!file || !validateForm()) {
      setConfirmOpen(false);
      return;
    }

    setSaving(true);
    setSubmitProgress(0);
    try {
      const formData = new FormData();
      formData.append("student_name", studentName.trim());
      formData.append("student_id", studentId.trim());
      formData.append("pathshala_name", pathshalaName.trim());
      formData.append("phone_number", phoneNumber.trim());
      formData.append("birth_date", birthDate);
      formData.append("age", age);
      if (topicId) {
        formData.append("topic", topicId);
      }
      formData.append("remark", remark.trim());
      formData.append("video_title", videoTitle.trim());
      formData.append("video_attachment", file);

      const response = await axiosConfig.post(
        "/rajnagar_gyanmahostav/student-video-submissions/",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: event => {
            if (!event.total) {
              return;
            }
            const pct = Math.round((event.loaded * 100) / event.total);
            // Cap at 99 until the server responds successfully.
            setSubmitProgress(Math.min(pct, 99));
          },
        }
      );

      setSubmitProgress(100);

      const payload = response.data?.data ?? response.data ?? {};
      const reference =
        payload.reference ||
        payload.reference_number ||
        payload.id ||
        `RGU-${Math.floor(Math.random() * 10000)}`;

      setConfirmOpen(false);
      setReceipt({
        id: String(reference),
        studentName: studentName.trim(),
        studentId: studentId.trim(),
        videoTitle: videoTitle.trim(),
        fileName: file.name,
        fileSizeMB: Math.round((file.size / (1024 * 1024)) * 10) / 10,
        status: "Pending",
        submittedAt: new Date().toISOString(),
        timeline: [
          { at: new Date().toISOString(), label: "Submission created" },
          { at: new Date().toISOString(), label: "Status set to Pending" },
        ],
      });
      toast.success("Submission saved successfully");
      reset();
    } catch (error: unknown) {
      console.error(error);
      setSubmitProgress(0);
      toast.error(extractApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const fileSizeLabel = file
    ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
    : "";

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className="bg-card shadow-card rounded-2xl border p-6 sm:p-8"
      >
        <div className="grid gap-5">
          <div>
            <Label htmlFor="name">Student Name *</Label>
            <Input
              id="name"
              maxLength={100}
              placeholder="Enter your full name"
              value={studentName}
              onChange={e => {
                setStudentName(e.target.value);
                clearError("studentName");
              }}
            />
            {errors.studentName && (
              <p className="text-destructive mt-1 text-xs">
                {errors.studentName}
              </p>
            )}
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <Label htmlFor="sid">Student ID *</Label>
              <Input
                id="sid"
                maxLength={50}
                placeholder="Enter your Student ID"
                value={studentId}
                type="number"
                inputMode="numeric"
                onChange={e => {
                  setStudentId(e.target.value);
                  clearError("studentId");
                }}
              />
              {errors.studentId && (
                <p className="text-destructive mt-1 text-xs">
                  {errors.studentId}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="title">Video Title *</Label>
              <Input
                id="title"
                maxLength={150}
                placeholder="Enter video title"
                value={videoTitle}
                onChange={e => {
                  setVideoTitle(e.target.value);
                  clearError("videoTitle");
                }}
              />
              {errors.videoTitle && (
                <p className="text-destructive mt-1 text-xs">
                  {errors.videoTitle}
                </p>
              )}
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <Label htmlFor="pathshala">Pathshala Name *</Label>
              <Popover
                open={pathshalaOpen}
                onOpenChange={open => {
                  setPathshalaOpen(open);
                  if (!open) setPathshalaSearch("");
                }}
              >
                <PopoverTrigger asChild>
                  <Button
                    id="pathshala"
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={pathshalaOpen}
                    className="h-10 w-full justify-between font-normal"
                  >
                    <span
                      className={cn(
                        "truncate",
                        !pathshalaName && "text-muted-foreground"
                      )}
                    >
                      {pathshalaName || "Select your pathshala"}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="w-(--radix-popover-trigger-width) p-0"
                >
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search pathshala…"
                      value={pathshalaSearch}
                      onValueChange={setPathshalaSearch}
                    />
                    <CommandList onScroll={handlePathshalaListScroll}>
                      <CommandEmpty>
                        {pathshalasLoading
                          ? "Searching…"
                          : "No pathshala found."}
                      </CommandEmpty>
                      <CommandGroup>
                        {pathshalas.map(p => (
                          <CommandItem
                            key={p.id}
                            value={p.name}
                            onSelect={() => {
                              setPathshalaName(p.name);
                              clearError("pathshalaName");
                              setPathshalaOpen(false);
                              setPathshalaSearch("");
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                pathshalaName === p.name
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />
                            <span className="truncate">{p.name}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                      {pathshalasLoadingMore && (
                        <p className="text-muted-foreground py-2 text-center text-xs">
                          Loading more…
                        </p>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {errors.pathshalaName && (
                <p className="text-destructive mt-1 text-xs">
                  {errors.pathshalaName}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                type="tel"
                inputMode="numeric"
                maxLength={10}
                placeholder="Enter 10-digit phone number"
                value={phoneNumber}
                onChange={e => {
                  setPhoneNumber(
                    e.target.value.replace(/\D/g, "").slice(0, 10)
                  );
                  clearError("phoneNumber");
                }}
              />
              {errors.phoneNumber && (
                <p className="text-destructive mt-1 text-xs">
                  {errors.phoneNumber}
                </p>
              )}
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <Label htmlFor="dob">Date of Birth *</Label>
              <DatePicker
                id="dob"
                className={cn(
                  "!h-9 w-full !rounded-md !border-input !bg-transparent !shadow-sm",
                  "hover:!border-input focus-within:!border-ring focus-within:!ring-ring focus-within:!shadow-none focus-within:!ring-1",
                  "[&_.ant-picker-input>input]:!h-full [&_.ant-picker-input>input]:!text-sm",
                  "[&_.ant-picker-input>input]:placeholder:!text-muted-foreground"
                )}
                styles={{
                  root: {
                    borderRadius: "var(--radius-md)",
                    height: "2.25rem",
                  },
                }}
                style={{ width: "100%" }}
                format="DD-MM-YYYY"
                placeholder="Select date of birth"
                allowClear
                inputReadOnly
                value={
                  birthDate ? dayjs(birthDate, "YYYY-MM-DD") : null
                }
                disabledDate={(current: Dayjs) =>
                  !!current && current.isAfter(dayjs(), "day")
                }
                onChange={(date: Dayjs | null) => {
                  setBirthDate(date ? date.format("YYYY-MM-DD") : "");
                  clearError("birthDate");
                }}
              />
              {errors.birthDate && (
                <p className="text-destructive mt-1 text-xs">
                  {errors.birthDate}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="age">Age</Label>
              <Input
                id="age"
                disabled
                readOnly
                placeholder="Auto-filled from date of birth"
                value={age ? `${age} years` : ""}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="topic">Topic</Label>
            <Select
              value={topicId}
              onValueChange={setTopicId}
              disabled={!age || topicsLoading || topics.length === 0}
            >
              <SelectTrigger id="topic">
                <SelectValue
                  placeholder={
                    !age
                      ? "Select your date of birth first"
                      : topicsLoading
                        ? "Loading topics…"
                        : topics.length === 0
                          ? "No topics available for your age"
                          : "Select a topic"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {topics.map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                    {t.size != null ? ` (max ${t.size} MB)` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground mt-1 text-xs">
              Topics are shown based on your age.
            </p>
          </div>

          <div>
            <Label htmlFor="remark">Remark</Label>
            <Textarea
              id="remark"
              maxLength={500}
              rows={3}
              placeholder="Any additional notes (optional)"
              value={remark}
              onChange={e => {
                setRemark(e.target.value);
                clearError("remark");
              }}
            />
            {errors.remark && (
              <p className="text-destructive mt-1 text-xs">{errors.remark}</p>
            )}
          </div>

          <div className="min-w-0">
            <Label>Video Attachment *</Label>
            <div
              onDragOver={e => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={e => {
                e.preventDefault();
                setDragging(false);
                const f = e.dataTransfer.files?.[0];
                if (f) {
                  handleFile(f);
                }
              }}
              className={cn(
                "mt-1 flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-6 text-center transition-colors",
                dragging
                  ? "border-primary bg-royal-soft"
                  : "border-border bg-muted/30",
                errors.file && "border-destructive/60 bg-destructive/5"
              )}
            >
              {!file ? (
                <>
                  <div className="bg-royal-soft text-royal grid h-12 w-12 place-items-center rounded-full">
                    <Upload className="h-5 w-5" />
                  </div>
                  <div className="mt-3 text-sm font-medium">
                    Drag & drop your video here
                  </div>
                  <div className="text-muted-foreground text-xs">or</div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => inputRef.current?.click()}
                  >
                    Browse Video
                  </Button>
                  <input
                    ref={inputRef}
                    type="file"
                    className="hidden"
                    accept="video/mp4,video/quicktime,video/x-msvideo,video/webm,.mp4,.mov,.avi,.webm"
                    onChange={e => handleFile(e.target.files?.[0] ?? null)}
                  />
                </>
              ) : (
                <div className="w-full min-w-0">
                  <div className="bg-background flex min-w-0 items-center justify-between gap-3 rounded-lg border p-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="bg-royal-soft text-royal grid h-10 w-10 shrink-0 place-items-center rounded-lg">
                        <FileVideo className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">
                          {file.name}
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {(file.size / (1024 * 1024)).toFixed(1)} MB
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setFile(null);
                        setProgress(0);
                      }}
                      className="text-muted-foreground hover:bg-muted rounded-md p-1.5"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  {uploading && (
                    <Progress value={progress} className="mt-3 h-1.5" />
                  )}
                  <div className="mt-2 flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => inputRef.current?.click()}
                    >
                      Replace
                    </Button>
                    <input
                      ref={inputRef}
                      type="file"
                      className="hidden"
                      accept="video/mp4,video/quicktime,video/x-msvideo,video/webm,.mp4,.mov,.avi,.webm"
                      onChange={e => handleFile(e.target.files?.[0] ?? null)}
                    />
                  </div>
                </div>
              )}
            </div>
            <p className="text-muted-foreground mt-2 text-xs">
              Maximum allowed video size is {maxVideoMb} MB
              {selectedTopic?.size != null
                ? ` (for topic “${selectedTopic.name}”)`
                : ""}
              . Formats: MP4, MOV, AVI, WebM.
            </p>
            {errors.file && (
              <p className="text-destructive mt-1 text-xs">{errors.file}</p>
            )}
          </div>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={reset}>
              Reset
            </Button>
            <Button
              type="submit"
              disabled={saving || uploading}
              className="min-w-40"
            >
              {saving ? "Saving..." : "Save Submission"}
            </Button>
          </div>
        </div>
      </form>

      {/* Step 1: Confirm details before API save */}
      <Dialog
        open={confirmOpen}
        onOpenChange={o => {
          if (!saving) {
            setConfirmOpen(o);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="bg-royal-soft text-royal mx-auto mb-2 grid h-14 w-14 place-items-center rounded-full">
              <FileVideo className="h-7 w-7" />
            </div>
            <DialogTitle className="text-center text-2xl text-[#0f2a6b]">
              Confirm Submission
            </DialogTitle>
            <DialogDescription className="text-center">
              Please review your details below, then confirm to save your video
              submission.
            </DialogDescription>
          </DialogHeader>

          <div className="bg-muted/40 mt-1 space-y-2.5 rounded-xl border p-4 text-sm">
            <Row k="Student Name" v={studentName.trim()} />
            <Row k="Student ID" v={studentId.trim()} />
            <Row k="Pathshala Name" v={pathshalaName.trim()} />
            <Row k="Phone Number" v={phoneNumber.trim()} />
            <Row k="Date of Birth" v={formatDobDisplay(birthDate)} />
            <Row k="Age" v={age ? `${age} years` : "-"} />
            {topicId ? (
              <Row
                k="Topic"
                v={topics.find(t => t.id === topicId)?.name ?? "-"}
              />
            ) : null}
            <Row k="Video Title" v={videoTitle.trim()} />
            {remark.trim() ? <Row k="Remark" v={remark.trim()} /> : null}
            <Row
              k="Video File"
              v={
                <>
                  <span className="break-words">{file?.name}</span>
                  {fileSizeLabel ? (
                    <span className="text-muted-foreground block text-xs font-normal">
                      {fileSizeLabel}
                    </span>
                  ) : null}
                </>
              }
            />
          </div>

          {saving ? (
            <div className="mt-3 space-y-2 rounded-xl border border-[#0f2a6b]/15 bg-[#0f2a6b]/5 p-3">
              <div className="flex items-center justify-between gap-3 text-sm">
                <span className="text-[#0f2a6b] font-medium">
                  Uploading video submission…
                </span>
                <span className="text-[#0f2a6b] tabular-nums font-semibold">
                  {submitProgress}%
                </span>
              </div>
              <Progress value={submitProgress} className="h-2" />
            </div>
          ) : null}

          <DialogFooter className="mt-2 flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              disabled={saving}
              onClick={() => setConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="min-w-40 bg-[#0f2a6b] hover:bg-[#0c2258]"
              disabled={saving}
              onClick={() => void handleConfirmSave()}
            >
              {saving ? `Uploading ${submitProgress}%` : "Confirm & Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Step 2: Success receipt after API save */}
      <Dialog open={!!receipt} onOpenChange={o => !o && setReceipt(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="bg-success/10 text-success mx-auto mb-2 grid h-14 w-14 place-items-center rounded-full">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <DialogTitle className="text-center text-2xl text-[#0f2a6b]">
              Thank You!
            </DialogTitle>
            <DialogDescription className="text-center">
              Your video has been submitted successfully for verification.
            </DialogDescription>
          </DialogHeader>
          {receipt && (
            <div className="bg-muted/40 mt-2 space-y-2 rounded-xl border p-4 text-sm">
              <Row k="Student Name" v={receipt.studentName} />
              <Row k="Student ID" v={receipt.studentId} />
              <Row k="Video Title" v={receipt.videoTitle} />
              <Row
                k="Submitted"
                v={new Date(receipt.submittedAt).toLocaleString()}
              />
              <Row
                k="Reference #"
                v={
                  <span className="text-royal font-mono font-semibold">
                    {receipt.id}
                  </span>
                }
              />
            </div>
          )}
          <div className="mt-2 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={() => setReceipt(null)}>
              Close
            </Button>
            <Button
              className="bg-[#0f2a6b] hover:bg-[#0c2258]"
              onClick={() => setReceipt(null)}
            >
              Submit Another Video
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Display HTML date (yyyy-mm-dd) as dd-mm-yyyy. */
function formatDobDisplay(isoDate: string): string {
  if (!isoDate) {
    return "-";
  }
  const [y, m, d] = isoDate.split("-");
  if (!y || !m || !d) {
    return isoDate;
  }
  return `${d}-${m}-${y}`;
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="text-muted-foreground shrink-0">{k}</div>
      <div className="text-foreground min-w-0 flex-1 text-right font-medium break-words">
        {v}
      </div>
    </div>
  );
}

function SiteFooter() {
  return (
    <footer className="bg-muted/30 border-t py-8">
      <div className="text-muted-foreground mx-auto max-w-7xl px-4 text-center text-sm">
        <div className="text-guj text-foreground font-semibold">
          શ્રી રાજનગર જૈન શ્વે ધા ઈનામી પરીક્ષા ટ્રસ્ટ
        </div>
        <div className="mt-1">
          <a
            href="tel:+919409354583"
            className="text-primary font-medium hover:text-blue-600"
          >
            9409354583
          </a>
        </div>
        <div className="mt-1">
          © {new Date().getFullYear()} શ્રી રાજનગર જૈન શ્વે ધા ઈનામી પરીક્ષા
          ટ્રસ્ટ. All rights reserved.
        </div>

        <div className="mt-6">
          <div
            className="border-border/60 mx-auto mb-6 w-full border-t"
            aria-hidden="true"
          />
          <div className="flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-[10px] font-medium tracking-[0.18em] uppercase">
                Powered by
              </span>
              <a
                href="https://www.universal-software.com"
                target="_blank"
                rel="noopener noreferrer"
                className="!text-primary text-[14px] font-semibold"
              >
                Universal Software
              </a>
            </div>
            <div
              className="bg-border/70 hidden h-9 w-px sm:block"
              aria-hidden="true"
            />
            <div className="bg-background rounded-lg border px-3 py-2 shadow-sm">
              <img
                src={usLogoSrc}
                alt="Universal Software"
                className="h-8 w-auto object-contain"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
